import {
    BaseQueryFn,
    createApi,
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { patternLibraryUrl } from '_config';
import { isUserLoggedIn } from '_services/LocalStorage/LoggedIn';
import { clearCatalogToken, getCatalogToken } from './catalogToken';
import { toLocale, toQuery } from './compat';
import {
    PatternLibraryRegistriesResponse,
    PatternLibrarySearchParams,
    PatternLibrarySearchResponse,
} from './types';

const rawBaseQuery = fetchBaseQuery({
    baseUrl: `${patternLibraryUrl}/api/v1/`,
    prepareHeaders: async (headers) => {
        const token = await getCatalogToken();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
    },
});

/**
 * A 401 means the held token is no longer good - expired under clock drift,
 * or the signing secret rotated. Mint a fresh one and retry once; a second
 * 401 is a real answer. Signed out there is nothing to refresh, so the 401
 * stands and the caller gates the UI on it.
 */
const baseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    const result = await rawBaseQuery(args, api, extraOptions);
    if (result.error?.status !== 401 || !isUserLoggedIn()) {
        return result;
    }
    clearCatalogToken();
    return rawBaseQuery(args, api, extraOptions);
};

/**
 * Read-only catalogue, bearer-gated since 2026-09-21. Cache-Control is
 * `private, max-age=N` with an ETag, so the browser cache still does the
 * revalidation; no ETag bookkeeping is ported from the app.
 */
export const PatternLibraryApi = createApi({
    reducerPath: 'api/patternLibrary',
    baseQuery,
    endpoints: (builder) => ({
        getRegistries: builder.query<
            PatternLibraryRegistriesResponse,
            { language: LanguageCode }
        >({
            query: ({ language }) =>
                `registries?${toQuery({ locale: toLocale(language) })}`,
            // iconUrl is root-relative on the wire
            transformResponse: (
                response: PatternLibraryRegistriesResponse,
            ) => ({
                ...response,
                browseGroups: response.browseGroups.map((group) => ({
                    ...group,
                    categories: group.categories.map((category) => ({
                        ...category,
                        iconUrl: category.iconUrl?.startsWith('/')
                            ? patternLibraryUrl + category.iconUrl
                            : category.iconUrl,
                    })),
                })),
            }),
        }),
        searchPatterns: builder.query<
            PatternLibrarySearchResponse,
            { params: PatternLibrarySearchParams; language: LanguageCode }
        >({
            query: ({ params, language }) =>
                `patterns/search?${toQuery({
                    locale: toLocale(language),
                    q: params.q,
                    sort: params.sort,
                    page: params.page,
                    pageSize: params.pageSize,
                    ...params.filters,
                })}`,
        }),
    }),
});

export const { useGetRegistriesQuery, useSearchPatternsQuery } =
    PatternLibraryApi;
